import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { useGetApiHealthHistoryQuery } from "../../../redux/apiHealth/apiHealth.api";
import { UptimeBar } from "./UptimeBar";

interface EndpointHistoryProps {
	contractKey: string;
}

/** The drill-down: the uptime strip plus what the most recent failing check actually saw. */
export const EndpointHistory: FunctionComponent<EndpointHistoryProps> = ({ contractKey }) => {
	const { t } = useTranslation();
	const { data, isLoading } = useGetApiHealthHistoryQuery({ contractKey });

	if (isLoading) {
		return <div className="py-3 text-xs text-gray-500">…</div>;
	}

	if (!data || data.entries.length === 0) {
		return <div className="py-3 text-xs text-gray-500">{t("apiHealth.detail.empty")}</div>;
	}

	const [latest] = data.entries;

	return (
		<div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
			<div className="mb-2 text-xs font-medium text-gray-700">{t("apiHealth.detail.title")}</div>

			<UptimeBar entries={data.entries} />

			<div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600">
				<span>
					{t("apiHealth.detail.mode")}: {t(`apiHealth.modes.${latest.mode}`)}
				</span>
				{latest.latencyMs !== null && (
					<span>
						{t("apiHealth.detail.latency")}: {latest.latencyMs}ms
					</span>
				)}
				{latest.sampleRequestId !== null && (
					<span>{t("apiHealth.detail.sample", { id: latest.sampleRequestId })}</span>
				)}
			</div>

			{latest.errorMessage && (
				<p className="mt-2 break-all font-mono text-xs text-red-700">{latest.errorMessage}</p>
			)}

			{latest.violations.length > 0 && (
				<div className="mt-2">
					<div className="text-xs font-medium text-gray-700">
						{t("apiHealth.detail.violations")}
					</div>
					<ul className="mt-1 space-y-0.5">
						{latest.violations.slice(0, 8).map((violation) => (
							<li
								key={`${violation.path}-${violation.message}`}
								className="font-mono text-xs text-gray-700"
							>
								<span className="text-red-700">{violation.path}</span>: {violation.message}
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};
